<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  components: {},
  props: {
    playerId: {
      type: Number,
      default: () => null,
      required: true,
    },
  },
  data() {
    return {
      loaded: false,
      players: null,
      headers: [
        {
          text: this.$t('name'),
          value: 'name',
        },
        {
          text: this.$t('age'),
          value: 'age',
        },
        {
          text: this.$t('similar_players.index'),
          value: 'index',
        },
        {
          text: this.$t('similar_players.ranking'),
          value: 'ranking',
        },
        {
          text: this.$t('similar_players.team'),
          value: 'team',
        },
        {
          text: this.$t('similar_players.tournament'),
          value: 'tournament',
        },
      ],
      tournaments: [],
      seasons: [],
      height: [
        '150',
        '155',
        '160',
        '165',
        '170',
        '175',
        '180',
        '185',
        '190',
        '195',
        '200',
        '205',
        '210',
      ],
      contractEnd: {
        year: null,
        nextYear: null,
        followingYears: [],
      },
      footValues: [
        {
          label: this.$t('similar_players.right'),
          value: 'right',
        },
        {
          label: this.$t('similar_players.left'),
          value: 'left',
        },
        {
          label: this.$t('similar_players.both'),
          value: 'both',
        },
      ],
      form: {
        position: null,
        foot: null,
        ageMin: null,
        ageMax: null,
        tournament: [],
        tiers: ['1'],
        contractEnd: null,
        heightMin: null,
        heightMax: null,
        indexMin: null,
        seasonStart: null,
      },
    }
  },
  computed: {
    ...mapGetters('ui', ['getDefaultSeason', 'getSeasons']),
    positionValues() {
      return this.players === null
        ? []
        : this.players
            // gather all positions and remove duplicates
            .reduce((positions, player) => {
              if (!positions.includes(player.position))
                positions.push(player.position)
              return positions
            }, [])
            .sort()
    },
    filteredPlayers() {
      return this.players.filter((player) => {
        if (this.form.heightMin !== null && player.height < this.form.heightMin)
          return false

        if (
          this.form.heightMax !== null &&
          player.height >= this.form.heightMax
        ) {
          return false
        }

        if (this.form.ageMin !== null && player.age < this.form.ageMin) {
          return false
        }

        if (
          this.form.foot !== null &&
          player.foot_name.toLowerCase() !== this.form.foot
        ) {
          return false
        }

        if (this.form.ageMax !== null && player.age >= this.form.ageMax) {
          return false
        }

        if (
          this.form.tournament.length && // if no tournament is selected
          !this.form.tournament.includes(player.competition_id)
        )
          return false

        const positionMatches = player.position.indexOf(this.form.position) >= 0
        if (this.form.position !== null && !positionMatches) return false

        if (
          this.form.contractEnd !== null &&
          typeof this.form.contractEnd !== 'undefined'
        ) {
          let contractEnd = new Date(this.form.contractEnd + '')
          contractEnd = contractEnd.setFullYear(contractEnd.getFullYear() + 1)

          if (new Date(player.contract_ending) < contractEnd) {
            return false
          }
        }

        if (player.index < this.form.indexMin) return false

        return true
      })
    },
    tablePlayers() {
      return this.players === null
        ? []
        : this.filteredPlayers.map((player) => {
            return {
              name: player.name,
              age: player.age,
              index: player.index,
              ranking: player.ranking,
              foot: player.foot_name,
              team: player.team,
              tournament: player.competition,
              percentage: player.percentage,
              compare: player.id,
            }
          })
    },
  },
  async created() {
    await this.getPlayerTournaments().then((response) => {
      this.tournaments = response
    })

    await this.getPlayerCompareData({ id: this.playerId }).then((response) => {
      this.players = response.players
      this.loaded = true
    })

    this.initFormData()
  },
  methods: {
    ...mapActions('players', ['getPlayerCompareData', 'getPlayerTournaments']),
    initFormData() {
      this.seasons = this.getSeasons
      this.form.seasonStart = this.getDefaultSeason

      const today = new Date()

      this.contractEnd.year = today.getFullYear() + 1
      this.contractEnd.nextYear = today.getFullYear() + 2
      this.contractEnd.followingYears = [3, 4, 5, 6, 7, 8, 9, 10].map(
        (num) => today.getFullYear() + num
      )

      this.form.position = null
      this.form.position = null
      this.form.foot = null
      this.form.ageMin = null
      this.form.ageMax = null
      this.form.tournament = []
      this.form.tiers = ['1']
      this.form.contractEnd = null
      this.form.heightMin = null
      this.form.heightMax = null
      this.form.indexMin = null
    },
    clearFilters() {
      this.initFormData()
      this.reloadPlayers()
    },
    updateSeasonStart() {
      this.reloadPlayers()
    },
    reloadPlayers() {
      this.loaded = false

      this.getPlayerCompareData({
        id: this.playerId,
        season: this.form.seasonStart,
        tiers: this.form.tiers,
      }).then((response) => {
        this.players = response.players
        this.loaded = true
      })
    },
    changeTournament(item) {
      this.form.tournament = item
    },
  },
}
</script>

<template>
  <div class="mt-4">
    <h4 class="pb-2">{{ $t('similar_players_title') }}</h4>
    <v-sheet :class="$style.similiarPlayersContent">
      <v-row>
        <v-col lg="3" md="12" sm="12">
          <h5>{{ $t('filters') }}</h5>
          <hr :class="[$style.line, 'mb-2']" />
          <div :class="$style.clearBtnWrapper">
            <v-btn
              primary
              small
              :class="$style.clearBtn"
              @click="clearFilters"
              >{{ $t('similar_players.clear') }}</v-btn
            >
          </div>
          <v-form :class="['mt-4', $style.form]">
            <v-row>
              <v-col lg="6">
                <v-select
                  v-model="form.seasonStart"
                  dense
                  outlined
                  class=""
                  :items="seasons"
                  :label="$t('similar_players.season_start')"
                  :placeholder="$t('similar_players.season_start')"
                  hide-details
                  @change="updateSeasonStart"
                />
              </v-col>
              <v-col lg="6"> </v-col>
            </v-row>
            <v-row class="">
              <v-col lg="6">
                <v-text-field
                  v-model="form.ageMin"
                  outlined
                  dense
                  clearable
                  type="number"
                  min="0"
                  hide-details
                  :label="$t('similar_players.age_min')"
                  :placeholder="$t('similar_players.age_min')"
                />
              </v-col>
              <v-col lg="6">
                <v-text-field
                  v-model="form.ageMax"
                  clearable
                  outlined
                  dense
                  type="number"
                  min="0"
                  hide-details
                  :label="$t('similar_players.age_max')"
                  :placeholder="$t('similar_players.age_max')"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="form.tournament"
                  dense
                  outlined
                  clearable
                  multiple
                  chips
                  deletable-chips
                  small-chips
                  hide-details
                  item-text="tournament_name"
                  item-value="tournament_id"
                  :items="tournaments"
                  :label="$t('similar_players.tournament')"
                  :placeholder="$t('similar_players.tournament')"
                  @change="changeTournament"
                /> </v-col
            ></v-row>
            <v-row>
              <v-col>
                <p class="mb-1">{{ $t('similar_players.tier') }}</p>
                <v-checkbox
                  v-model="form.tiers"
                  :class="$style.checkbox"
                  dense
                  :label="$t('similar_players.league_primary')"
                  value="1"
                  hide-details
                  @change="reloadPlayers"
                ></v-checkbox>
                <v-checkbox
                  v-model="form.tiers"
                  :class="$style.checkbox"
                  dense
                  :label="$t('similar_players.league_secondary')"
                  value="2"
                  hide-details
                  @change="reloadPlayers"
                ></v-checkbox>
                <v-checkbox
                  v-model="form.tiers"
                  :class="$style.checkbox"
                  dense
                  :label="$t('similar_players.league_other')"
                  value="3"
                  hide-details
                  @change="reloadPlayers"
                ></v-checkbox> </v-col
            ></v-row>
            <v-row>
              <v-col>
                <p class="mb-1">{{ $t('similar_players.contract_end') }}</p>
                <v-btn-toggle
                  v-model="form.contractEnd"
                  class=""
                  dense
                  :class="$style.contractEndGroup"
                >
                  <v-btn :value="contractEnd.year">
                    {{ contractEnd.year }}
                  </v-btn>

                  <v-btn :value="contractEnd.nextYear">
                    {{ contractEnd.nextYear }}
                  </v-btn>

                  <v-select
                    v-model="form.contractEnd"
                    :class="$style.contractEndSelect"
                    dense
                    clearable
                    filled
                    :items="contractEnd.followingYears"
                    :placeholder="$t('similar_players.other_season')"
                    hide-details
                  />
                </v-btn-toggle> </v-col
            ></v-row>
            <v-row class="">
              <v-col lg="6">
                <v-select
                  v-model="form.heightMin"
                  outlined
                  dense
                  :items="height"
                  :label="$t('similar_players.height_min')"
                  :placeholder="$t('similar_players.height_min')"
                  hide-details
                />
              </v-col>
              <v-col lg="6">
                <v-select
                  v-model="form.heightMax"
                  outlined
                  dense
                  :items="height"
                  :label="$t('similar_players.height_max')"
                  :placeholder="$t('similar_players.height_max')"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <p class="mb-1">{{ $t('similar_players.min_index') }}</p>
                <v-slider
                  v-model="form.indexMin"
                  class="align-center"
                  max="100"
                  min="0"
                  hide-details
                  thumb-label
                >
                  <template v-slot:append>
                    <v-text-field
                      v-model="form.indexMin"
                      class="mt-0 pt-0"
                      hide-details
                      single-line
                      type="number"
                      style="width: 60px"
                    ></v-text-field>
                  </template>
                </v-slider> </v-col
            ></v-row>
            <v-row>
              <v-col>
                <v-select
                  v-model="form.foot"
                  dense
                  outlined
                  clearable
                  class=""
                  item-text="label"
                  item-value="value"
                  :items="footValues"
                  :label="$t('similar_players.foot')"
                  :placeholder="$t('similar_players.foot')"
                  hide-details
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-select
                  v-model="form.position"
                  dense
                  outlined
                  clearable
                  class=""
                  item-text="label"
                  item-value="value"
                  :items="positionValues"
                  :label="$t('similar_players.position')"
                  :placeholder="$t('similar_players.position')"
                  hide-details
                />
              </v-col>
            </v-row>
          </v-form>
        </v-col>
        <v-col lg="9" md="12" sm="12">
          <v-data-table
            class="v-data-table--font-xs"
            :headers="headers"
            :items="tablePlayers"
            :loading="!loaded"
          >
            <template slot="item.name" slot-scope="item">
              <router-link
                :to="{
                  name: 'player-profile',
                  params: { playerId: item.item.compare },
                }"
                :class="$style.noLink"
              >
                {{ item.item.name }}
              </router-link>
            </template>
            <template slot="item.index" slot-scope="item">
              <span :class="$style.indexClass">{{ item.item.index }}</span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-sheet>
  </div>
</template>

<style lang="scss" module>
@import '@design';

.form [class^='row'] {
  margin-top: 0;
}

.form [class^='col'] {
  padding: 10px 12px;
}

.clearBtn {
  margin-left: auto;
}

.clearBtnWrapper {
  display: flex;
  justify-content: right;
}

.similiarPlayersContent {
  padding: 12px;
  padding-top: 2rem;
}

.line {
  color: $color-secondary;
}

.contractEndSelect {
  font-size: 10px;
  border-top-left-radius: 0;

  > div > div {
    min-height: 35px !important;
  }
}

.contractEndGroup {
  width: 100%;

  button {
    // padding: 0 4px !important;
  }

  button span {
    font-size: 10px;
  }
}

.checkbox[class^='v-input'] {
  margin-top: 0;
}

.indexClass {
  color: $color-accent;
}

.noLink {
  color: $color-text !important;
  text-decoration: none;
}
</style>
